import { computed, defineComponent, ref } from 'vue'
import type { VNode } from 'vue'
import type { DialogServiceMethods } from 'primevue/dialogservice'
import Button from 'primevue/button'
import IftaLabel from 'primevue/iftalabel'
import Message from 'primevue/message'
import Textarea from 'primevue/textarea'
import { createDeferred } from '@/utils/deferred'
import { getWalkthroughEmbedUrl, getWalkthroughPrefillFromStore, useWalkthroughBooking, WALKTHROUGH_SCHEDULER_URL } from '@/composables/useWalkthroughBooking'
// @ts-expect-error importing untyped JS module into TypeScript
import { extractErrorMessage } from '@/utils/misc'
// @ts-expect-error importing untyped JS module into TypeScript
import { logEvent } from '@/services/clients/firebase'
// @ts-expect-error importing untyped JS module into TypeScript
import service from '@/modules/dashboard/services/FeedbackService'
// @ts-expect-error import path to avoid circular dependency issues
import store from '@/store'
import { classicSidebarLayout } from '@/components/Sidebar/sidebarLayout'
import './support-dialog.css'

type Step = 'triage' | 'sidebar' | 'sidebarApplied' | 'walkthrough' | 'feedback' | 'thanks'
type Choice = 'sidebar' | 'walkthrough' | 'feedback' | 'not_now'

// Someone who has already told us why they switch back should not be asked again every time,
// but a week is short enough that we still hear from anyone with a recurring problem.
// Bump the suffix to ask everyone again, e.g. when the prompt gains a new option.
// 2: the sidebar option.
const PROMPT_ANSWERED_AT_KEY = 'CLASSIC_REVERT_PROMPT_ANSWERED_AT_3'
const PROMPT_THROTTLE_MS = 7 * 24 * 60 * 60 * 1000

function wasRecentlyPrompted (): boolean {
  try {
    const answeredAt = Number(window.localStorage.getItem(PROMPT_ANSWERED_AT_KEY))
    return Number.isFinite(answeredAt) && answeredAt > 0 && Date.now() - answeredAt < PROMPT_THROTTLE_MS
  } catch {
    // Private browsing / blocked storage. Prompting is the safe fallback.
    return false
  }
}

function recordPrompted () {
  try {
    window.localStorage.setItem(PROMPT_ANSWERED_AT_KEY, String(Date.now()))
  } catch {
    // Nothing to do; worst case we ask again next time.
  }
}

export interface ClassicConsoleFeedbackOptions {
  /** Opens the sidebar's Customize mode. Called after the dialog has resolved false. */
  onCustomizeSidebar: () => void
}

/**
 * Asks a user switching back to the Classic console whether it is the unfamiliar sidebar (which
 * they can customize, or switch to the Classic layout in one click), whether they need a live
 * walkthrough, have a specific problem worth a support ticket, or just want to go.
 *
 * Resolves true to continue on to the Classic console, false to stay in the new one.
 */
export function promptClassicConsoleFeedback (
  dialog: DialogServiceMethods,
  { onCustomizeSidebar }: ClassicConsoleFeedbackOptions
): Promise<boolean> {
  if (wasRecentlyPrompted()) {
    logEvent('classic_revert_prompt_skipped')
    return Promise.resolve(true)
  }

  const deferred = createDeferred<boolean>()

  logEvent('classic_revert_prompt_shown')

  function finish (proceed: boolean) {
    if (!deferred.isPending) {
      return
    }
    if (proceed) {
      recordPrompted()
    }
    deferred.resolve(proceed)
    dialogRef.close()
  }

  const DialogContent = defineComponent({
    setup () {
      const step = ref<Step>('triage')
      const previousStep = ref<Step>('triage')
      const { booked: walkthroughBooked } = useWalkthroughBooking('classic_revert')
      const walkthroughEmbedUrl = getWalkthroughEmbedUrl(getWalkthroughPrefillFromStore())
      const comments = ref('')
      const saving = ref(false)
      const saveErrorMessage = ref<string | null>(null)

      const valid = computed(() => comments.value.trim().length > 0)

      // Whatever was stored before the Classic layout went in (null for the standard sidebar), so
      // it can be put back if they do not like it.
      const sidebarBeforeClassic = ref<unknown>(null)
      const sidebarSaving = ref(false)
      const sidebarErrorMessage = ref<string | null>(null)

      function goTo (next: Step) {
        previousStep.value = step.value
        step.value = next
      }

      function goBack () {
        step.value = previousStep.value
      }

      function choose (choice: Choice) {
        logEvent('classic_revert_choice', { choice })
        if (choice === 'not_now') {
          finish(true)
        } else {
          goTo(choice)
        }
      }

      function customizeSidebar () {
        logEvent('classic_revert_sidebar_action', { action: 'customize' })
        finish(false)
        onCustomizeSidebar()
      }

      async function saveSidebarLayout (sidebarLayout: unknown) {
        sidebarSaving.value = true
        sidebarErrorMessage.value = null
        try {
          await store.dispatch('patchOrgUserPreferences', { sidebarLayout })
          return true
        } catch (error) {
          sidebarErrorMessage.value = extractErrorMessage(error)
          return false
        } finally {
          sidebarSaving.value = false
        }
      }

      async function applyClassicSidebar () {
        logEvent('classic_revert_sidebar_action', { action: 'classic_layout' })
        const previous = store.state.orgUserPreferences?.sidebarLayout ?? null
        if (await saveSidebarLayout(classicSidebarLayout())) {
          sidebarBeforeClassic.value = previous
          goTo('sidebarApplied')
        }
      }

      async function undoClassicSidebar () {
        logEvent('classic_revert_sidebar_action', { action: 'undo' })
        if (await saveSidebarLayout(sidebarBeforeClassic.value)) {
          // Back from the sidebar options should lead to the start, not to the layout just undone.
          previousStep.value = 'triage'
          step.value = 'sidebar'
        }
      }

      async function save () {
        saving.value = true
        saveErrorMessage.value = null

        try {
          await service.submitFeedback(null, `classic console revert feedback: ${comments.value.trim()}`)
        } catch (error) {
          saveErrorMessage.value = extractErrorMessage(error)
          return
        } finally {
          saving.value = false
        }

        logEvent('classic_revert_feedback_submitted')
        goTo('thanks')
      }

      const backButton = () => (
        <Button
          type="button"
          size="small"
          class="tw:shrink-0"
          severity="secondary"
          text
          onClick={goBack}
        >
          <font-awesome-icon icon="arrow-left" class="tw:mr-1" />
          <span>Back</span>
        </Button>
      )

      const choiceCard = (icon: string, title: string, subtitle: string, onClick: () => void, disabled = false) => (
        <button type="button" class="choice-card" disabled={disabled} onClick={onClick}>
          <font-awesome-icon icon={icon} class="choice-card-icon" />
          <span class="choice-card-body">
            <span class="choice-card-title">{title}</span>
            <span class="choice-card-subtitle">{subtitle}</span>
          </span>
          <font-awesome-icon icon="chevron-right" class="choice-card-chevron" />
        </button>
      )

      // Step 1: figure out which kind of person is leaving.
      const renderTriage = () => (
        <div class="feedback-step feedback-step-narrow">
          <p class="tw:mb-4 tw:text-sm">
            You're about to switch back to the Classic view. Which of these sounds closest?
          </p>

          <Message
            severity="info"
            size="small"
            closable={false}
            class="tw:mb-4"
            v-slots={{ icon: () => <font-awesome-icon icon="circle-info" /> }}
          >
            The Classic console will be retired after December 31, 2026. We'd love to help you feel
            at home in the new one before then — and your feedback helps us get it right.
          </Message>

          {choiceCard(
            'table-columns',
            'The sidebar menu is different or hard to find things in',
            'Make it look like the Classic menu in one click, or arrange it your own way',
            () => choose('sidebar')
          )}

          {choiceCard(
            'video',
            'I\'m not sure how to do things in the new console',
            'Book a free 15-minute video call — a support agent will screen-share and show you around',
            () => choose('walkthrough')
          )}

          {choiceCard(
            'comment-dots',
            'Something specific is missing or not working',
            'Tell us what it is — it goes straight to our support team',
            () => choose('feedback')
          )}

          <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:mt-4">
            <Button
              type="button"
              size="small"
              label="Just switch me back"
              severity="secondary"
              text
              onClick={() => choose('not_now')}
            />
            <Button
              type="button"
              size="small"
              label="Stay in new console"
              severity="secondary"
              outlined
              onClick={() => finish(false)}
            />
          </div>
        </div>
      )

      const sidebarError = () => sidebarErrorMessage.value !== null && (
        <div class="tw:mt-2 tw:text-red-500 tw:text-right tw:text-sm">
          <font-awesome-icon icon="triangle-exclamation" class="tw:mr-1" />
          <span>{sidebarErrorMessage.value}</span>
        </div>
      )

      // Step 2a: the sidebar is customizable, including back to the Classic layout.
      const renderSidebar = () => (
        <div class="feedback-step feedback-step-narrow">
          <div class="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:mb-3">
            <p class="tw:min-w-0 tw:mb-0 tw:text-sm">
              The new sidebar is yours to arrange. Put back the menu you're used to, or set it up
              however suits you best — it only changes your own sidebar.
            </p>
            {backButton()}
          </div>

          {choiceCard(
            'clock-rotate-left',
            'Match the Classic console menu',
            'One click: Manage, Reports and Settings menus, named and ordered as in Classic',
            applyClassicSidebar,
            sidebarSaving.value
          )}

          {choiceCard(
            'pen-to-square',
            'Customize the sidebar myself',
            'Drag items into place, rename them, add pages you use often and remove the rest',
            customizeSidebar,
            sidebarSaving.value
          )}

          {sidebarError()}

          <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:mt-4">
            <Button
              type="button"
              size="small"
              label="Switch to Classic anyway"
              severity="secondary"
              text
              disabled={sidebarSaving.value}
              onClick={() => finish(true)}
            />
            <Button
              type="button"
              size="small"
              label="Stay in new console"
              severity="secondary"
              outlined
              disabled={sidebarSaving.value}
              onClick={() => finish(false)}
            />
          </div>
        </div>
      )

      // Step 2a, done: the Classic layout is in place. Let them look at it before deciding.
      const renderSidebarApplied = () => (
        <div class="feedback-step feedback-step-narrow">
          <div class="tw:flex tw:items-start tw:gap-2 tw:mb-4 tw:text-sm">
            <font-awesome-icon icon="circle-check" class="tw:text-green-600 tw:mt-1" />
            <span>
              Done — your sidebar now follows the Classic menu: <b>Manage</b>, <b>Reports</b> and{' '}
              <b>Settings</b>. You can fine-tune it any time with <b>Customize sidebar</b> at the
              bottom of the menu.
            </span>
          </div>

          {sidebarError()}

          <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:mt-4">
            <div class="tw:flex tw:flex-wrap tw:gap-2">
              <Button
                type="button"
                size="small"
                label="Undo"
                severity="secondary"
                text
                disabled={sidebarSaving.value}
                loading={sidebarSaving.value}
                onClick={undoClassicSidebar}
              />
              <Button
                type="button"
                size="small"
                label="Fine-tune it"
                severity="secondary"
                text
                disabled={sidebarSaving.value}
                onClick={customizeSidebar}
              />
            </div>
            <div class="tw:flex tw:flex-wrap tw:gap-2">
              <Button
                type="button"
                size="small"
                label="Switch to Classic anyway"
                severity="secondary"
                outlined
                disabled={sidebarSaving.value}
                onClick={() => finish(true)}
              />
              <Button
                type="button"
                size="small"
                label="Stay in new console"
                disabled={sidebarSaving.value}
                onClick={() => finish(false)}
              />
            </div>
          </div>
        </div>
      )

      // Step 2b: embedded Zoom scheduler.
      const renderWalkthrough = () => (
        <div class="feedback-step feedback-step-wide">
          <div class="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:mb-3">
            <div class="tw:min-w-0 tw:text-sm">
              <div class="tw:mb-1">
                Pick a time that works for you. A support agent will screen-share and show you where
                everything moved and how to get your day-to-day done faster.
              </div>
              <a href={WALKTHROUGH_SCHEDULER_URL} target="_blank" rel="noopener" class="tw:text-xs">
                Open the scheduler in a new tab
                <font-awesome-icon icon="arrow-up-right-from-square" class="tw:ml-1" />
              </a>
            </div>
            {backButton()}
          </div>

          <div class="scheduler-frame-wrapper">
            <iframe src={walkthroughEmbedUrl} title="Schedule a console walkthrough" />
          </div>

          { walkthroughBooked.value &&
          <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-3 tw:text-sm">
            <font-awesome-icon icon="circle-check" class="tw:text-green-600" />
            <span>Great — you'll get a calendar invite by email.</span>
          </div>
          }

          <div class="tw:flex tw:flex-wrap tw:justify-end tw:gap-2 tw:mt-4">
            <Button
              type="button"
              size="small"
              label="Switch to Classic"
              severity="secondary"
              outlined
              onClick={() => finish(true)}
            />
            <Button type="button" size="small" label="Stay in new console" onClick={() => finish(false)} />
          </div>
        </div>
      )

      // Step 2c: a real, specific issue -> Intercom conversation.
      const renderFeedback = () => (
        <div class="feedback-step feedback-step-narrow">
          <div class="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:mb-3">
            <p class="tw:min-w-0 tw:mb-0 tw:text-sm">
              What's missing or not working? The more specific you can be, the better — this
              goes to our support team and we'll follow up by email.
            </p>
            {backButton()}
          </div>

          <IftaLabel class="tw:block tw:w-full">
            <Textarea
              id="classic-console-feedback"
              v-model={comments.value}
              class="tw:w-full"
              size="small"
              rows="5"
              style="resize: none"
              disabled={saving.value}
            />
            <label for="classic-console-feedback">What went wrong?</label>
          </IftaLabel>

          <div class="tw:flex tw:flex-wrap tw:justify-end tw:gap-2 tw:mt-4">
            <Button
              type="button"
              size="small"
              label="Stay in new console"
              severity="secondary"
              outlined
              disabled={saving.value}
              onClick={() => finish(false)}
            />
            <Button
              type="button"
              size="small"
              label="Send and switch to Classic"
              disabled={saving.value || !valid.value}
              loading={saving.value}
              onClick={save}
            />
          </div>

          { saveErrorMessage.value !== null &&
          <div class="tw:mt-2 tw:text-red-500 tw:text-right tw:text-sm">
            <font-awesome-icon icon="triangle-exclamation" class="tw:mr-1" />
            <span>{saveErrorMessage.value}</span>
          </div>
          }
        </div>
      )

      // Step 3: feedback landed. Offer the walkthrough once more before they go.
      const renderThanks = () => (
        <div class="feedback-step feedback-step-narrow">
          <div class="tw:flex tw:items-start tw:gap-2 tw:mb-4 tw:text-sm">
            <font-awesome-icon icon="circle-check" class="tw:text-green-600 tw:mt-1" />
            <span>Thanks — this is on its way to our support team, and we'll follow up by email.</span>
          </div>

          {choiceCard(
            'video',
            'Want to walk through it live?',
            'Book a free 15-minute video call — a support agent will screen-share and show you around',
            () => choose('walkthrough')
          )}

          <div class="tw:flex tw:flex-wrap tw:justify-end tw:gap-2 tw:mt-4">
            <Button
              type="button"
              size="small"
              label="Stay in new console"
              severity="secondary"
              outlined
              onClick={() => finish(false)}
            />
            <Button type="button" size="small" label="Continue to Classic" onClick={() => finish(true)} />
          </div>
        </div>
      )

      const renderers: Record<Step, () => VNode> = {
        triage: renderTriage,
        sidebar: renderSidebar,
        sidebarApplied: renderSidebarApplied,
        walkthrough: renderWalkthrough,
        feedback: renderFeedback,
        thanks: renderThanks
      }

      return () => <div class="support-dialog">{renderers[step.value]()}</div>
    }
  })

  const dialogRef = dialog.open(DialogContent, {
    props: {
      header: 'Before switching to Classic',
      pt: {
        root: 'support-dialog-shell tw:!overflow-hidden tw:!rounded-xl'
      },
      modal: true,
      closable: true,
      dismissableMask: false,
      closeOnEscape: true
    },
    onClose () {
      // Covers the header close button and Escape, which both mean "stay in the new console".
      if (deferred.isPending) {
        deferred.resolve(false)
      }
    }
  })

  deferred.promise.then(proceeded => {
    logEvent('classic_revert_outcome', { outcome: proceeded ? 'classic' : 'stayed' })
  })

  return deferred.promise
}
